import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { getPrices } from '../services/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area } from 'recharts';
import './PortfolioChart.css';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function PortfolioChart({ holdings, balance }) {
  const { user } = useAuth();
  const [prices, setPrices] = useState({});
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      const { data } = await supabase
        .from('portfolio_snapshots')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
      setSnapshots(data || []);
    };
    fetchSnapshots();
  }, [user.id]);

  useEffect(() => {
    const fetchPrices = async () => {
      if (holdings.length === 0) return;
      try {
        const tickers = holdings.map((h) => h.ticker);
        const data = await getPrices(tickers);
        const map = {};
        (data.tickers || []).forEach((t) => {
          map[t.ticker] = t;
        });
        setPrices(map);
      } catch {}
    };
    fetchPrices();
  }, [holdings]);

  if (holdings.length === 0) {
    return (
      <div className="portfolio-chart empty-state">
        <h2>Your Portfolio</h2>
        <p>Buy some stocks to see your portfolio breakdown here.</p>
      </div>
    );
  }

  // Allocation pie data
  const holdingValues = holdings.map((h) => {
    const currentPrice = prices[h.ticker]?.price || h.avg_price;
    return {
      name: h.ticker,
      value: parseFloat((currentPrice * h.shares).toFixed(2)),
    };
  });
  const pieData = [
    ...holdingValues,
    { name: 'Cash', value: parseFloat(balance.toFixed(2)) },
  ];
  const totalAssets = pieData.reduce((sum, d) => sum + d.value, 0);

  // P&L bar data
  const plData = holdings.map((h) => {
    const currentPrice = prices[h.ticker]?.price || h.avg_price;
    const pl = (currentPrice - h.avg_price) * h.shares;
    return {
      ticker: h.ticker,
      pl: parseFloat(pl.toFixed(2)),
      fill: pl >= 0 ? '#10b981' : '#ef4444',
    };
  });

  const totalPL = plData.reduce((sum, d) => sum + d.pl, 0);
  const totalPLPct = totalAssets > balance ? ((totalPL / (totalAssets - balance - totalPL)) * 100) : 0;

  return (
    <div className="portfolio-chart">
      <div className="portfolio-summary">
        <div className="summary-item">
          <span className="summary-label">Total Assets</span>
          <span className="summary-value">${totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total P&L</span>
          <span className={`summary-value ${totalPL >= 0 ? 'positive' : 'negative'}`}>
            {totalPL >= 0 ? '+' : ''}${totalPL.toFixed(2)} ({totalPLPct.toFixed(2)}%)
          </span>
        </div>
      </div>

      <div className="portfolio-charts-grid">
        <div className="chart-box">
          <h3 className="chart-box-title">Asset Allocation</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={50}
                paddingAngle={2}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9' }}
                formatter={(v) => [`$${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Value']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-box">
          <h3 className="chart-box-title">Profit / Loss by Stock</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={plData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="ticker" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={55} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9' }}
                formatter={(v) => [`$${v.toFixed(2)}`, 'P&L']}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Bar dataKey="pl" radius={[4, 4, 0, 0]}>
                {plData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {snapshots.length > 0 && (
        <div className="chart-box" style={{ marginTop: 16 }}>
          <h3 className="chart-box-title">Portfolio Value History</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={snapshots.map(s => ({
              date: new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              total: s.total_value,
              cash: s.cash,
              stocks: s.stocks_value,
            }))}>
              <defs>
                <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={55} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9' }}
                formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === 'total' ? 'Total' : name === 'cash' ? 'Cash' : 'Stocks']}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Area type="monotone" dataKey="total" stroke="#10b981" fill="url(#portfolioGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
